import { useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { testimonials } from '@/mocks/articles';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9splip4ne17ei0sdlr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const serverMsg = parsed?.meta?.message || parsed?.message || '';

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const values = [
    { title: '专业严谨', description: '所有课程由资深从业者设计，基于超过十年的行业经验，确保教学内容的专业性和实用性。', icon: 'ri-award-line' },
    { title: '实践导向', description: '注重实操训练，模拟真实服务场景，让学员在培训阶段就积累丰富的实践经验。', icon: 'ri-tools-line' },
    { title: '个性培养', description: '小班授课，因材施教，关注每位学员的个人特点和成长需求，提供一对一指导。', icon: 'ri-user-heart-line' },
    { title: '终身学习', description: '为校友提供持续的学习资源和行业动态更新，支持职业发展的每一个阶段。', icon: 'ri-lightbulb-line' },
  ];

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于女仆教育',
          description: '女仆教育创立于2018年，致力于培养专业、优雅、高素质的女仆服务人才，推动中国女仆服务行业的标准化与专业化发展',
          url: 'https://hkxwhcm.cn/about',
        }}
      />
      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://hkxwhcm.cn/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: '关于我们',
            },
          ],
        }}
      />
      {/* Brand Intro */}
      <section className="relative h-[400px] md:h-[480px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cream%20colored%20elegant%20Japanese%20style%20training%20academy%20interior%20with%20tall%20windows%20and%20flowing%20sheer%20curtains%2C%20wooden%20floor%2C%20vintage%20furniture%2C%20soft%20natural%20light%2C%20refined%20and%20peaceful%20atmosphere%2C%20editorial%20interior%20photography&width=1920&height=1080&seq=about-hero-01&orientation=landscape"
            alt="关于女仆教育"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
        </div>
        <div className="relative z-10 w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4">
            关于女仆教育
          </h1>
          <p className="text-sm md:text-lg text-background-50/80 max-w-2xl mx-auto leading-relaxed">
            创立于2018年，致力于培养专业、优雅、高素质的女仆服务人才，推动中国女仆服务行业的标准化与专业化发展
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="py-4">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>
      </div>

      {/* Brand Story */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                品牌故事
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  女仆教育成立于2018年，是国内首批专注于女仆职业培训的教育机构。我们的创始人曾在日本深造女仆服务与管理多年，深知专业培训对于提升行业服务质量的重要性。
                </p>
                <p>
                  多年来，我们不断优化课程体系，引进国际先进的女仆服务标准，结合中国本土市场的实际需求，打造了一套科学、系统、实用的培训方案。从基础礼仪到高级管家服务，每一门课程都凝聚了资深从业者的心血与智慧。
                </p>
                <p>
                  截至目前，我们已累计培养超过2000名专业女仆人才，学员遍布高端家政服务公司、五星级酒店、私人管家服务机构以及日式女仆咖啡厅等多元化就业领域。
                </p>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden h-64 md:h-80">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20maid%20training%20classroom%20with%20students%20practicing%20tea%20ceremony%20in%20a%20bright%20cream%20colored%20room%20with%20large%20windows%2C%20warm%20natural%20lighting%2C%20professional%20training%20atmosphere%2C%20Japanese%20aesthetic&width=800&height=600&seq=about-story-01&orientation=landscape"
                alt="女仆教育培训课堂"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              核心价值
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              我们坚守的教育理念与价值承诺
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {values.map((v) => (
              <div key={v.title} className="bg-background-50 rounded-xl p-5 md:p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-accent-100 flex items-center justify-center">
                  <i className={`${v.icon} text-accent-500 text-xl md:text-2xl`}></i>
                </div>
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2">{v.title}</h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              学员口碑
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              听听往期学员的真实声音
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-background-100 rounded-xl p-5 md:p-6">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  &ldquo;{t.content}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-primary-700 text-sm font-medium">{t.name.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                    <div className="text-xs text-foreground-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              联系我们
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              如果您对课程有任何疑问，欢迎随时与我们联系
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-10">
            <div className="lg:col-span-2 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">地址</h4>
                  <p className="text-sm text-foreground-600">上海市徐汇区衡山路888号<br />女仆教育培训中心 3F</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-primary-500 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">电话</h4>
                  <p className="text-sm text-foreground-600">400-888-9999</p>
                  <p className="text-xs text-foreground-500 mt-1">周一至周五 9:00-18:00</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-primary-500 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">邮箱</h4>
                  <p className="text-sm text-foreground-600">info@hkxwhcm.cn</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-xl p-5 md:p-6 space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电话
                  </label>
                  <input
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的联系电话"
                  />
                </div>

                <div>
                  <label htmlFor="contact-course" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    感兴趣的课程
                  </label>
                  <select
                    id="contact-course"
                    name="course"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors cursor-pointer"
                  >
                    <option value="">请选择课程</option>
                    <option value="liyi-guifan">礼仪规范课程</option>
                    <option value="fuwu-jineng">服务技能课程</option>
                    <option value="zhiye-fazhan">职业发展课程</option>
                    <option value="hangye-zhishi">行业知识课程</option>
                    <option value="other">其他咨询</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入您想咨询的内容（限500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-hp"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="whitespace-nowrap w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  {submitting ? '提交中...' : '提交咨询'}
                </button>

                {formStatus === 'success' && (
                  <div className="p-3 rounded-lg bg-accent-100 text-accent-800 text-sm text-center">
                    提交成功！我们会尽快与您联系。
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm text-center">
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="bg-background-50">
        <div className="h-[400px] w-full">
          <iframe
            title="女仆教育培训中心位置"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3416.889264365!2d121.446!3d31.194!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzHCsDExJzM4LjQiTiAxMjHCsDI2JzQ1LjYiRQ!5e0!3m2!1szh-CN!2scn!4v1"
            className="w-full h-full border-0"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </section>
    </div>
  );
}